import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { usePrevious } from 'react-delta';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { LogEntry, Permission } from 'common/graphqlTypes';
import { UrlParams } from 'common/types';
import { applySettingsToUrl } from 'common/util/settings';
import { buildUrl, getLogLink, getParserLink } from 'common/util/location';
import { hasPermissionState } from 'components/account/account.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { absoluteResolvedUrlState } from 'components/common/Managers/RouteStateManager.state';
import { useCopyShortLink } from 'hooks/useCopyShortLink';

import { isSafari } from 'service/browser';

import Button from 'components/common/Button/Button';
import Link from 'components/common/Link/Link';
import Toolbar from 'components/common/Toolbar/Toolbar';
import LogDetailsFilter from './LogDetailsFilter';

import './LogDetailsActionButtons.scss';

type LogDetailsActionButtonProps = {
  logEntry:                LogEntry;
  continuationToken:       string;
  enableLogSearchActions?: boolean;
  fieldFilter:             string;
  onClose:                 () => void;
  setFieldFilter:          (filter: string) => void;
};

function LogDetailsActionButtons({
  logEntry,
  continuationToken,
  enableLogSearchActions,
  fieldFilter,
  onClose,
  setFieldFilter,
}: LogDetailsActionButtonProps): JSX.Element {
  const settings = useRecoilValue(settingsState);
  const parsersReadOnly = !useRecoilValue(hasPermissionState(Permission.fullAccess));
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const centeredUrl = buildUrl(absoluteUrl, {
    [K.URL_PARAM.CONTINUATION_TOKEN]: continuationToken,
    [K.URL_PARAM.CENTERED]: 'true',
  } as UrlParams);
  const urlToShorten = applySettingsToUrl(centeredUrl, settings);
  const prevContinuationToken = usePrevious(continuationToken);

  const {
    copyLinkIsAvailable,
    copyShortLink,
    fetchShortUrlForSafari,
  } = useCopyShortLink(urlToShorten);

  useEffect(() => {
    if (isSafari) {
      if (prevContinuationToken !== continuationToken) {
        fetchShortUrlForSafari();
      }
    }
  }, [continuationToken, prevContinuationToken]);

  // returns href for pod log link
  const getPodLogLink = (podName: string, podNamespace: string): string => {
    // TODO: Unify and complete "generateLogSearch" log behavior {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-3862}
    // This function handles part of this task, but stuck on how to include the k8s labels into the existing layout.
    const msTimestamp = logEntry?.timestamp ? Math.round(logEntry.timestamp / 1000000) : null;
    return buildUrl(K.ROUTE.EVENTS, {
      [K.URL_PARAM.START_TIME]: msTimestamp ? String(msTimestamp - K.ONE_HOUR) : '',
      [K.URL_PARAM.END_TIME]: msTimestamp ? String(msTimestamp + K.ONE_HOUR) : '',
      [K.URL_PARAM.CENTERED]: 'true',
      [K.URL_PARAM.CONTINUATION_TOKEN]: continuationToken || '',
      [K.URL_PARAM.HIGHLIGHT_EVENT]: continuationToken || '',
      [K.URL_PARAM.FILTER]: `$pod_name = "${podName}" $pod_namespace = "${podNamespace}"`,
    } as UrlParams);
  };

  const actionButtons = [];

  if (enableLogSearchActions) {
    const podName = logEntry?.values?.pod_name || logEntry?.serverInfo?.pod_name;
    const podNamespace = logEntry?.values?.pod_namespace || logEntry?.serverInfo?.pod_namespace;
    if (!!podName && !!podNamespace) {
      const podLogLink = getPodLogLink(podName, podNamespace);
      actionButtons.push((
        <Link href={podLogLink} onClick={onClose}>
          <Button size="sm" testId={ELEMENT.inspectFieldsDrawerPodLog}>
            Pod Log
          </Button>
        </Link>
      ));
    }

    const logViewWindow = settings[K.SETTING.LOG_VIEW_WINDOW] / K.NS_PER_MS;
    const now = (new Date()).getTime();
    const originalLogLink = getLogLink(logEntry, continuationToken, logViewWindow, now);
    const threadLogLink = getLogLink(logEntry, continuationToken, logViewWindow, now, logEntry?.threadId || '');

    actionButtons.push(
      (
        <Button
          size="sm"
          testId={ELEMENT.inspectFieldsDrawerCopyButton}
          onClick={copyShortLink}
          disabled={!copyLinkIsAvailable}
        >
          Copy Link
        </Button>
      ),
      (
        <Link href={originalLogLink}>
          <Button size="sm" testId={ELEMENT.inspectFieldsDrawerOriginalLog}>
            See in Original Log
          </Button>
        </Link>
      ),
      (
        <Link href={threadLogLink}>
          <Button size="sm" testId={ELEMENT.inspectFieldsDrawerThreadLog}>
            See in Thread Log
          </Button>
        </Link>
      ),
    );

    const parserLink = getParserLink(logEntry);
    if (parserLink) {
      actionButtons.push((
        <Link href={parserLink} onClick={onClose}>
          <Button size="sm" testId={ELEMENT.inspectFieldsDrawerParser}>
            {parsersReadOnly ? 'View Parser' : 'Edit Parser'}
          </Button>
        </Link>
      ));
    }
  }

  const fieldFilterInput = <LogDetailsFilter fieldFilter={fieldFilter} setFieldFilter={setFieldFilter} />;

  return (
    <Toolbar
      className="LogDetailsActionButtons"
      leftItems={[ fieldFilterInput ]}
      rightItems={actionButtons}
    />
  );
}

export default LogDetailsActionButtons;
